"use client";

import Image from "next/image";
import { RefreshCw, ServerCrash } from "lucide-react";
import { useEffect } from "react";
import errorImage from "@/images/svg/error_image.svg";
import { clearAllOperationalAlerts } from "@/lib/operational-alert-events";

type SystemErrorScreenProps = {
  onRetry: () => void;
};

export function SystemErrorScreen({ onRetry }: SystemErrorScreenProps) {
  useEffect(() => {
    clearAllOperationalAlerts();
  }, []);

  return (
    <main className="flex min-h-screen items-center bg-[var(--color-background)] text-[var(--color-text-primary)]">
      <div className="mx-auto grid w-full max-w-6xl items-center gap-10 px-6 py-10 sm:px-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(24rem,1.1fr)] lg:gap-16 lg:px-16">
        <section className="order-2 max-w-xl lg:order-1" aria-labelledby="system-error-title">

          <h1
            id="system-error-title"
            className="mt-6 max-w-lg text-4xl font-semibold tracking-[-0.04em] text-[var(--color-text-primary)] sm:text-5xl"
          >
            Não foi possível carregar o sistema.
          </h1>
          <p className="mt-5 max-w-lg text-base leading-7 text-[var(--color-text-secondary)] sm:text-lg">
            O serviço pode estar temporariamente indisponível. Tente novamente
            em alguns instantes.
          </p>

          <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[var(--color-brand-primary)] px-5 text-sm font-semibold text-white transition hover:bg-[var(--color-brand-primary-dark)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent)] focus-visible:ring-offset-2"
            >
              <RefreshCw aria-hidden="true" className="h-4 w-4" />
              Tentar novamente
            </button>
          </div>

          <p className="mt-7 flex items-start gap-2 text-sm leading-6 text-[var(--color-text-muted)] font-bold">
            <ServerCrash aria-hidden="true" className="mt-1 h-4 w-4 shrink-0" />
            Se o problema persistir, entre em contato com o suporte.
          </p>
        </section>

        <div className="order-1 flex items-center justify-center lg:order-2">
          <Image
            src={errorImage}
            alt="Ilustração de uma falha temporária no sistema"
            priority
            className="h-auto w-full max-w-[34-rem]"
            draggable="false"
          />
        </div>
      </div>
    </main>
  );
}
